import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { Alert, Button, Dialog, FormControl, InputLabel, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, useMediaQuery } from "@mui/material";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { formatGBP2 } from "../../../components/Features";
import PageHeader from "../../../components/PageHeader";
import TablePaginationComponent from "../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import styles from "../shop.module.css";
import StockAdjustPopup from "./StockAdjustPopup";

const stockLabel = (item) => {
  if (parseInt(item.track_stock) !== 1) return <span className={`${styles.SubText}`}>Not tracked</span>;
  const qty = parseInt(item.stock_qty) || 0;
  if (qty <= 0) return <span className={`${styles.StockOut}`}>Out of stock</span>;
  if (qty <= parseInt(item.low_stock_threshold)) return <span className={`${styles.StockLow}`}>{qty} (low)</span>;
  return qty;
};

// Stock levels for shop items; changes are logged as stock movements
const ShopInventory = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const isMobile = useMediaQuery("(max-width:768px)");
  const canAdjust = auth?.user_type === 2 || auth?.permissions?.indexOf("shop:inventory") > -1;
  const { websiteData } = useWebsite();
  useSiteBranding("Shop inventory", websiteData);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ search: "", stock: "" });
  const [refresh, setRefresh] = useState(0);
  const [adjustItem, setAdjustItem] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  const debouncedSearch = useMemo(() => debounce((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(0);
  }, 500), []);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shop/inventory", {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
      params: { page: page + 1, per_page: rowsPerPage, ...filters },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setRows(response.data.data.data || []);
        setTotal(response.data.data.total || 0);
        setSummary(response.data.data.summary || {});
      }
    }).catch(() => { });
  }, [API_TOKEN, page, rowsPerPage, filters, refresh]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const notify = (message, type) => setSnackbar({ open: true, message, type });

  const cards = [
    { key: "", label: "Items", value: summary.items || 0 },
    { key: "low", label: "Low Stock", value: summary.low || 0 },
    { key: "out", label: "Out Of Stock", value: summary.out || 0 },
    { key: "value", label: "Stock Value", value: formatGBP2(summary.stock_value) },
  ];

  return (<>
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Inventory" trail={[{ label: "Shop" }, { label: "Inventory" }]} />
      <div className={`${styles.MainCard}`}>
        <div className={`${styles.SummaryGrid}`} style={{ gridTemplateColumns: isMobile ? undefined : "repeat(4, 1fr)" }}>
          {cards.map((c) => (<button type="button" key={c.label} className={`${styles.SummaryCard} ${c.key && filters.stock === c.key ? styles.SummaryCardActive : ""}`} onClick={() => c.key !== "value" && setFilter("stock", filters.stock === c.key ? "" : c.key)}>
            <h2>{c.value}</h2>
            <p>{c.label}</p>
          </button>))}
        </div>

        <div className={`${styles.FilterBar}`}>
          <div className={`${styles.SearchBox}`}>
            <TextField fullWidth size="small" placeholder="Search item or SKU" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }} />
          </div>
          <div className={`${styles.FilterItem}`}>
            <FormControl fullWidth size="small">
              <InputLabel>Stock</InputLabel>
              <Select label="Stock" value={filters.stock} onChange={(e) => setFilter("stock", e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="in">In stock</MenuItem>
                <MenuItem value="low">Low stock</MenuItem>
                <MenuItem value="out">Out of stock</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {!isMobile && (<TableContainer component={Paper} className={`${styles.TableCard}`}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledMainTableCell>Item</StyledMainTableCell>
                <StyledMainTableCell>SKU</StyledMainTableCell>
                <StyledMainTableCell align="right">Price</StyledMainTableCell>
                <StyledMainTableCell align="right">In Stock</StyledMainTableCell>
                <StyledMainTableCell align="right">Low Stock At</StyledMainTableCell>
                {canAdjust && <StyledMainTableCell></StyledMainTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className={`${styles.EmptyCell}`}>No items found. Add items from Shop &gt; Items.</TableCell></TableRow>}
              {rows.map((row) => (<StyledMainTableRow hover key={row.id}>
                <TableCell><span className={`${styles.ItemCell}`}>{row.image ? <img src={row.image} alt="" className={`${styles.Thumb}`} /> : <span className={`${styles.Thumb}`} />}{row.title}</span></TableCell>
                <TableCell>{row.sku || "-"}</TableCell>
                <TableCell align="right">{formatGBP2(row.amount)}</TableCell>
                <TableCell align="right">{stockLabel(row)}</TableCell>
                <TableCell align="right">{parseInt(row.track_stock) === 1 ? row.low_stock_threshold : "-"}</TableCell>
                {canAdjust && <TableCell align="right"><Button size="small" className={`${styles.ActionBU}`} onClick={() => setAdjustItem(row)}>Adjust stock</Button></TableCell>}
              </StyledMainTableRow>))}
            </TableBody>
            {total > 0 && <TableFooter>
              <TableRow>
                <TablePaginationComponent count={total} colSpan={6} page={page} rowsPerPage={rowsPerPage} handleChangePage={(e, p) => setPage(p)} handleChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
              </TableRow>
            </TableFooter>}
          </Table>
        </TableContainer>)}

        {isMobile && (<div className={`${styles.CardList}`}>
          {rows.length === 0 && <div className={`${styles.EmptyCell}`}>No items found.</div>}
          {rows.map((row) => (<div className={`${styles.Card}`} key={row.id}>
            <div className={`${styles.CardHead}`}>
              <span className={`${styles.ItemCell}`}>{row.image && <img src={row.image} alt="" className={`${styles.Thumb}`} />}<strong>{row.title}</strong></span>
              {canAdjust && <Button size="small" className={`${styles.ActionBU}`} onClick={() => setAdjustItem(row)}>Adjust</Button>}
            </div>
            <div className={`${styles.CardRow}`}><span>In stock</span><strong>{stockLabel(row)}</strong></div>
            <div className={`${styles.CardRow}`}><span>Price</span><strong>{formatGBP2(row.amount)}</strong></div>
          </div>))}
          {total > 0 && <TablePaginationComponent count={total} page={page} rowsPerPage={rowsPerPage} handleChangePage={(e, p) => setPage(p)} handleChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />}
        </div>)}
      </div>
    </div>

    <Dialog open={!!adjustItem} onClose={() => setAdjustItem(null)} fullWidth maxWidth="sm">
      {adjustItem && <StockAdjustPopup item={adjustItem} onClose={() => setAdjustItem(null)} notify={notify} onSaved={() => { setAdjustItem(null); setRefresh((r) => r + 1); }} />}
    </Dialog>

    <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: isMobile ? "center" : "right" }}>
      <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.type} variant="filled" sx={{ width: "100%" }}>{snackbar.message}</Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(ShopInventory);
